import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  createAssetProcessingDoc,
  exportDomToPdf,
  failAssetDoc,
  finalizeAssetDoc,
  uploadBlobToStorage,
} from './printablesExport';
import {
  formatBytes,
  getImageDimensions,
  loadQrStandeePrefs,
  saveQrStandeePrefs,
  uploadStandeeQrToStorage,
  validateQrFile,
} from './qrStandeeUploadUtils';
import './QRStandeePrintable.css';

const PAGE_SIZE_MM = { width: 210, height: 297 };
const MAX_QR_SIZE_LABEL = '2MB max / PNG or JPG only / 600x600px minimum';

const buildMissing = ({ compliance, profile, branch, qrImageUrl }) => {
  const missing = [];
  if (!qrImageUrl) missing.push('qr');
  if (compliance.requirePhone && !profile.phone) missing.push('phone');
  if (compliance.requireAddress && !branch.address) missing.push('address');
  if (compliance.requireRera && !branch.reraId) missing.push('rera');
  return missing;
};

const resolveStatusLabel = (missing) => {
  if (missing.includes('qr')) return 'Missing QR';
  if (missing.includes('phone')) return 'Missing Phone';
  if (missing.includes('address')) return 'Missing Address';
  if (missing.includes('rera')) return 'Missing RERA';
  return 'Ready';
};

const QRStandeePrintable = ({
  firebase,
  authUser,
  tenantId,
  branchId,
  profile,
  branch,
  brandKit,
  assignedEmail,
  assignedEmailMissing,
  compliance,
}) => {
  const pageRef = useRef(null);
  const fileInputRef = useRef(null);

  const [qrImageUrl, setQrImageUrl] = useState('');
  const [qrMeta, setQrMeta] = useState({
    fileName: '',
    sizeLabel: '',
    width: 0,
    height: 0,
    updatedAt: null,
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');

  const [saveToAssets, setSaveToAssets] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  const missing = useMemo(
    () => buildMissing({ compliance, profile, branch, qrImageUrl }),
    [branch, compliance, profile, qrImageUrl],
  );
  const isBlocked = missing.length > 0;
  const statusLabel = resolveStatusLabel(missing);
  const statusTone = isBlocked ? 'error' : 'success';
  const canSave = Boolean(tenantId && branchId && authUser?.uid);

  useEffect(() => {
    let active = true;

    const loadPrefs = async () => {
      if (!tenantId || !authUser?.uid || !firebase) return;
      try {
        const prefs = await loadQrStandeePrefs(firebase, tenantId, authUser.uid);
        if (!active || !prefs) return;
        setQrImageUrl(prefs.qrImageUrl || '');
        setQrMeta({
          fileName: prefs.fileName || 'QR image',
          sizeLabel: prefs.fileSize ? formatBytes(prefs.fileSize) : 'N/A',
          width: prefs.width || 0,
          height: prefs.height || 0,
          updatedAt: prefs.updatedAt || null,
        });
      } catch (loadErr) {
        console.error('QR standee prefs load failed:', loadErr);
      }
    };

    loadPrefs();
    return () => {
      active = false;
    };
  }, [authUser?.uid, firebase, tenantId]);

  const handleFilePick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError('');
    setUploadMessage('');

    const validation = validateQrFile(file);
    if (!validation.isValid) {
      setUploadError(validation.errors.join(' '));
      return;
    }

    try {
      const dimensions = await getImageDimensions(file);
      if (dimensions.width < 600 || dimensions.height < 600) {
        setUploadError('Minimum resolution is 600x600px for print quality.');
        return;
      }

      if (!firebase || !tenantId || !authUser?.uid) {
        setUploadError('Upload unavailable. Please try again later.');
        return;
      }

      setUploading(true);
      const uploadResult = await uploadStandeeQrToStorage({
        firebase,
        tenantId,
        uid: authUser.uid,
        file,
      });

      const meta = {
        fileName: file.name || 'standee-qr',
        fileSize: file.size,
        width: dimensions.width,
        height: dimensions.height,
      };

      await saveQrStandeePrefs(firebase, tenantId, authUser.uid, {
        qrImageUrl: uploadResult.qrImageUrl,
        storagePath: uploadResult.storagePath,
        ...meta,
      });

      setQrImageUrl(uploadResult.qrImageUrl);
      setQrMeta({
        fileName: meta.fileName,
        sizeLabel: formatBytes(meta.fileSize),
        width: meta.width,
        height: meta.height,
        updatedAt: new Date().toISOString(),
      });
      setUploadMessage('QR image uploaded.');
    } catch (err) {
      console.error('Standee QR upload failed:', err);
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleExport = async () => {
    if (isBlocked || !pageRef.current) return;
    setExporting(true);
    setError('');
    setWarning('');
    setMessage('');

    let assetRef = null;
    try {
      const fileName = `Douglas-McCartney-QR-Standee-${profile.fullName || 'franchisee'}.pdf`;
      const blob = await exportDomToPdf({
        element: pageRef.current,
        fileName,
        pageSizeMm: PAGE_SIZE_MM,
        backgroundColor: '#ffffff',
        marginMm: 8,
      });

      if (saveToAssets) {
        if (!canSave) {
          setWarning('Upload failed, file not saved.');
        } else {
          try {
            const snapshot = {
              qrImageUrl,
              phone: profile.phone || '',
              assignedEmail: assignedEmail || '',
              address: compliance.requireAddress ? branch.address || '' : '',
              reraId: compliance.requireRera ? branch.reraId || '' : '',
            };

            assetRef = await createAssetProcessingDoc({
              firebase,
              tenantId,
              branchId,
              authUserId: authUser?.uid || '',
              type: 'qr_standee',
              snapshot,
            });

            const storagePath = `tenants/${tenantId}/assets/${assetRef.id}/qr-standee.pdf`;
            const url = await uploadBlobToStorage({
              firebase,
              storagePath,
              blob,
              contentType: 'application/pdf',
              metadata: { tenantId, branchId, createdBy: authUser?.uid || '' },
            });

            await finalizeAssetDoc({
              firebase,
              assetRef,
              output: { pdfUrl: url, storagePath },
            });
          } catch (uploadError) {
            console.error('QR standee asset upload failed:', uploadError);
            setWarning('Upload failed, file not saved.');
            if (assetRef) {
              await failAssetDoc({ firebase, assetRef, errorMessage: uploadError.message });
            }
          }
        }
      }
      setMessage('QR standee downloaded.');
    } catch (err) {
      console.error('QR standee export failed:', err);
      setError('Could not generate PDF. Try again.');
    } finally {
      setExporting(false);
    }
  };

  const accentColor = brandKit?.colors?.accent || '#c8a951';
  const headlineFont = brandKit?.fonts?.primary || 'var(--font-serif, serif)';
  const bodyFont = brandKit?.fonts?.secondary || 'var(--font-sans, sans-serif)';
  const showAddress = compliance.requireAddress && branch.address;
  const showRera = compliance.requireRera && branch.reraId;

  return (
    <div className="printable-layout">
      <div className="printable-preview">
        <div className="printable-page qrstandee-page" ref={pageRef}>
          <div className="qrstandee-front" style={{ fontFamily: bodyFont }}>
            <div className="qrstandee-top">
              <div className="qrstandee-logo">
                {brandKit?.logoUrl ? (
                  <img src={brandKit.logoUrl} alt="Logo" crossOrigin="anonymous" />
                ) : (
                  <div className="qrstandee-logo-text">Douglas &amp; McCartney</div>
                )}
              </div>
              <div className="qrstandee-accent" style={{ backgroundColor: accentColor, opacity: 0.25 }} />
            </div>

            <div className="qrstandee-headline" style={{ fontFamily: headlineFont }}>
              Scan and Enquire
            </div>
            <div className="qrstandee-subheadline">INSTANT ACCESS TO LISTINGS AND SUPPORT</div>

            <div className="qrstandee-qr-card">
              {qrImageUrl ? (
                <img
                  src={qrImageUrl}
                  alt="QR Code"
                  className="qrstandee-qr-image"
                  crossOrigin="anonymous"
                />
              ) : (
                <div className="qrstandee-qr-placeholder">Upload QR to preview</div>
              )}
            </div>

            <div className="qrstandee-cta">Scan to WhatsApp</div>

            <div className="qrstandee-contact">
              <span>{profile.phone || 'Phone required'}</span>
              <span className="qrstandee-dot" style={{ backgroundColor: accentColor }} />
              <span>{assignedEmail || 'Email pending'}</span>
            </div>
          </div>

          <div className="qrstandee-flap" style={{ fontFamily: bodyFont }}>
            <div className="qrstandee-fold">
              <span className="qrstandee-scissors" aria-hidden="true">
                <svg viewBox="0 0 64 24" aria-hidden="true">
                  <circle cx="10" cy="12" r="5" stroke="#777" strokeWidth="2" fill="none" />
                  <circle cx="24" cy="12" r="5" stroke="#777" strokeWidth="2" fill="none" />
                  <path d="M29 12h25" stroke="#777" strokeWidth="2" strokeDasharray="4 6" />
                </svg>
              </span>
              <span>FOLD HERE</span>
            </div>
            <div className="qrstandee-instruction">Fold along the line and place on your desk.</div>
            {(showAddress || showRera) && (
              <div className="qrstandee-compliance">
                {showAddress && <div>{branch.address}</div>}
                {showRera && <div>RERA: {branch.reraId}</div>}
              </div>
            )}
            <div className="qrstandee-watermark">
              {brandKit?.logoUrl ? (
                <img src={brandKit.logoUrl} alt="Brand" crossOrigin="anonymous" />
              ) : (
                <span>Douglas &amp; McCartney</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="printable-panel">
        <div className={`printable-status ${statusTone}`}>{statusLabel}</div>
        {assignedEmailMissing && (
          <div className="printable-banner warning">Assigned email not set yet.</div>
        )}
        {missing.includes('qr') && (
          <div className="printable-banner error">Upload QR image to continue.</div>
        )}
        {missing.includes('phone') && (
          <div className="printable-banner error">
            Phone required.
            <button type="button" className="printable-link" onClick={() => window.location.assign('/account')}>
              Fix in Profile
            </button>
          </div>
        )}
        {missing.includes('address') && (
          <div className="printable-banner error">
            Address required.
            <button type="button" className="printable-link" onClick={() => window.location.assign('/account')}>
              Fix in Profile
            </button>
          </div>
        )}
        {missing.includes('rera') && (
          <div className="printable-banner error">
            RERA required.
            <button type="button" className="printable-link" onClick={() => window.location.assign('/account')}>
              Fix in Profile
            </button>
          </div>
        )}
        {uploadError && <div className="printable-banner error">{uploadError}</div>}
        {uploadMessage && <div className="printable-banner info">{uploadMessage}</div>}
        {message && <div className="printable-banner info">{message}</div>}
        {warning && <div className="printable-banner warning">{warning}</div>}
        {error && <div className="printable-banner error">{error}</div>}

        <div className="qrstandee-section">
          <h4>QR Image</h4>
          <div className="qrstandee-upload">
            <button
              type="button"
              className="printable-action"
              onClick={handleFilePick}
              disabled={uploading}
            >
              {qrImageUrl ? 'Replace QR' : 'Upload QR Image'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <div className="qrstandee-upload-hint">{MAX_QR_SIZE_LABEL}</div>
          </div>
          <div className="qrstandee-meta">
            <div>
              Status: <strong>{qrImageUrl ? 'Uploaded' : 'Not uploaded'}</strong>
            </div>
            <div>File: {qrMeta.fileName || 'N/A'}</div>
            <div>
              Resolution:{' '}
              {qrMeta.width && qrMeta.height ? `${qrMeta.width}x${qrMeta.height}px` : 'N/A'}
            </div>
            <div>Size: {qrMeta.sizeLabel || 'N/A'}</div>
          </div>
        </div>

        <div className="qrstandee-section">
          <h4>Contact</h4>
          <div className="printable-detail">
            <span>Phone</span>
            <strong>{profile.phone || 'Missing'}</strong>
          </div>
          <div className="printable-detail">
            <span>Assigned Email</span>
            <strong>{assignedEmail || 'Pending'}</strong>
          </div>
        </div>

        <div className="printable-actions">
          <label className="printable-checkbox">
            <input
              type="checkbox"
              checked={saveToAssets}
              onChange={(event) => setSaveToAssets(event.target.checked)}
            />
            Save to Assets Library
          </label>
          <button
            type="button"
            className="printable-action"
            disabled={exporting || isBlocked}
            onClick={handleExport}
          >
            {exporting ? 'Generating...' : 'Download Standee PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRStandeePrintable;
